import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const MASTER_ROUTES_DOC_ID = "current";
const getMasterRoutesDocRef = (db, podcastId, nodeId) =>
  doc(db, "podcasts", podcastId, "podcast_details", nodeId, "master_routes", MASTER_ROUTES_DOC_ID);

const extractTagValue = (source, tag) => {
  if (!source) return "";
  const regex = new RegExp(`<${tag}>[\\s\\S]*?<\\/${tag}>`, "i");
  const match = source.match(regex);
  if (!match) return "";
  return match[0].replace(new RegExp(`<\\/?${tag}>`, "gi"), "").trim();
};

const parseMasterRoutesXml = (xml) => {
  const raw = String(xml || "");
  if (!raw.trim()) return { entries: [], map: {}, xml: "" };
  const blocks = raw.split(/<Exit option>/i).slice(1);
  const entries = [];
  blocks.forEach(chunk => {
    const block = chunk.split(/<\/Exit option>/i)[0];
    if (!block) return;
    const id = extractTagValue(block, "id");
    const title = extractTagValue(block, "title");
    const key = extractTagValue(block, "key");
    const order = extractTagValue(block, "order");
    const routeMatch = block.match(/<Route>([\s\S]*?)<\/Route>/i);
    const routeBody = routeMatch ? routeMatch[1] : "";
    const linkBlocks = routeBody ? routeBody.split(/<Link>/i).slice(1) : [];
    const links = linkBlocks
      .map(segment => {
        const linkContent = segment.split(/<\/Link>/i)[0] || "";
        const linkId = extractTagValue(linkContent, "id");
        const linkName = extractTagValue(linkContent, "name");
        if (!linkId && !linkName) return null;
        return { id: linkId || "", name: linkName || "" };
      })
      .filter(Boolean);
    entries.push({
      id: (id || "").trim(),
      title: (title || "").trim(),
      key: (key || "").trim(),
      order: (order || "").trim(),
      links,
    });
  });
  const map = {};
  entries.forEach(entry => {
    if (entry.id) map[String(entry.id)] = entry;
    if (entry.key) map[String(entry.key)] = entry;
  });
  return { entries, map, xml: raw };
};

const entriesToMasterRoutesXml = (entries) => {
  if (!Array.isArray(entries) || entries.length === 0) return "";
  const lines = [];
  entries.forEach((entry) => {
    const id = String(entry?.id ?? "").trim();
    const title = String(entry?.title ?? "").trim();
    const key = String(entry?.key ?? "").trim();
    const order = String(entry?.order ?? "").trim();
    const links = Array.isArray(entry?.links) ? entry.links : [];
    if (!id && !title && !key && links.length === 0) return;

    lines.push("<Exit option>");
    lines.push(`  <id>${id}</id>`);
    lines.push(`  <title>${title}</title>`);
    lines.push(`  <key>${key}</key>`);
    if (order) lines.push(`  <order>${order}</order>`);
    lines.push("  <Route>");
    links.forEach((link) => {
      const linkId = String(link?.id ?? "").trim();
      const linkName = String(link?.name ?? "").trim();
      if (!linkId && !linkName) return;
      lines.push("    <Link>");
      lines.push(`      <id>${linkId}</id>`);
      lines.push(`      <name>${linkName}</name>`);
      lines.push("    </Link>");
    });
    lines.push("  </Route>");
    lines.push("</Exit option>");
    lines.push("");
  });
  return lines.join("\n").trim();
};

const normalizeEntries = (entries) =>
  (Array.isArray(entries) ? entries : []).map((entry) => ({
    id: String(entry?.id ?? "").trim(),
    title: String(entry?.title ?? "").trim(),
    key: String(entry?.key ?? "").trim(),
    order: String(entry?.order ?? "").trim(),
    links: (Array.isArray(entry?.links) ? entry.links : []).map((link) => ({
      id: String(link?.id ?? "").trim(),
      name: String(link?.name ?? "").trim(),
    })),
  }));

const reindexEntries = (entries) =>
  (Array.isArray(entries) ? entries : []).map((entry, idx) => ({
    ...entry,
    order: String(idx + 1),
  }));

const MasterRoutesPanel = ({
  podcastId,
  nodeId,
  autoChecklistItems,
  routeLinkSnapshots,
  graphData,
  onMasterRoutesChange,
  onMasterRoutesSaved,
  onActiveDestinationChange,
  routeEditState,
  onStartRouteEdit,
  onCancelRouteEdit,
  onSaveRouteEdit,
  onRemoveRouteEditLink,
  onFlipRouteEditLink,
}) => {
  const [masterRoutesMsg, setMasterRoutesMsg] = useState("");
  const [isSavingMasterRoutes, setIsSavingMasterRoutes] = useState(false);
  const [isLoadingMasterRoutes, setIsLoadingMasterRoutes] = useState(false);
  const [needsMasterRoutesBootstrap, setNeedsMasterRoutesBootstrap] = useState(false);
  const [editableEntries, setEditableEntries] = useState([]);
  const [expandedExitIdx, setExpandedExitIdx] = useState(null);
  const [editingExitIdx, setEditingExitIdx] = useState(null);
  const [isSavingRouteEdit, setIsSavingRouteEdit] = useState(false);
  const [selectedExitKeys, setSelectedExitKeys] = useState(() => new Set());

  const emitMasterRoutesChange = useCallback((xmlText) => {
    if (typeof onMasterRoutesChange !== "function") return;
    const parsed = parseMasterRoutesXml(xmlText);
    onMasterRoutesChange(parsed);
  }, [onMasterRoutesChange]);

  const emitMasterRoutesSaved = useCallback((reason) => {
    if (typeof onMasterRoutesSaved !== "function") return;
    onMasterRoutesSaved({ reason });
  }, [onMasterRoutesSaved]);

  const linkLookup = useMemo(() => {
    const map = new Map();
    if (Array.isArray(graphData?.links)) {
      graphData.links.forEach(row => {
        if (row && row.link_id != null) {
          map.set(String(row.link_id), row);
        }
      });
    }
    return map;
  }, [graphData]);

  const routeSnapshotsByNode = useMemo(() => {
    const map = new Map();
    (routeLinkSnapshots || []).forEach(route => {
      const nodeKey = String(route?.destNodeId ?? "");
      if (!nodeKey || map.has(nodeKey)) return;
      const ids = Array.isArray(route?.pathLinks)
        ? route.pathLinks.filter(id => id !== undefined && id !== null)
        : [];
      if (!ids.length) return;
      map.set(nodeKey, ids);
    });
    return map;
  }, [routeLinkSnapshots]);

  const canRegenerateMasterRoutes = routeLinkSnapshots?.some(route =>
    Array.isArray(route?.pathLinks) && route.pathLinks.length > 0
  );

  const getEntrySelectionKey = useCallback((entry, idx) => {
    const id = String(entry?.id || "").trim();
    const key = String(entry?.key || "").trim();
    return id || key || `idx-${idx}`;
  }, []);

  const buildMasterRoutesXml = useCallback(() => {
    if (!routeSnapshotsByNode.size) return "";

    const lines = [];
    const seen = new Set();

    const pushExit = (nodeKey, meta) => {
      if (!nodeKey || seen.has(nodeKey)) return;
      const linkIds = routeSnapshotsByNode.get(nodeKey);
      if (!linkIds || !linkIds.length) return;
      seen.add(nodeKey);

      const exitId = String(meta?.id ?? meta?.nodeId ?? nodeKey);
      const title = String(meta?.title || `Exit ${nodeKey}`);
      const exitKey = String(meta?.key ?? exitId);

      lines.push(`<Exit option>`);
      lines.push(`  <id>${exitId}</id>`);
      lines.push(`  <title>${title}</title>`);
      lines.push(`  <key>${exitKey}</key>`);
      lines.push(`  <order>${seen.size}</order>`);
      lines.push(`  <Route>`);

      linkIds.forEach(rawId => {
        const id = String(rawId);
        if (!id) return;
        const linkRow = linkLookup.get(id);
        const linkName = linkRow?.name || linkRow?.street_name || "";
        lines.push(`    <Link>`);
        lines.push(`      <id>${id}</id>`);
        lines.push(`      <name>${linkName}</name>`);
        lines.push(`    </Link>`);
      });

      lines.push(`  </Route>`);
      lines.push(`</Exit option>`);
      lines.push("");
    };

    (autoChecklistItems || []).forEach(item => {
      const nodeKey = String(item?.nodeId ?? item?.id ?? "");
      if (!nodeKey) return;
      pushExit(nodeKey, item);
    });

    routeSnapshotsByNode.forEach((_, nodeKey) => {
      if (seen.has(nodeKey)) return;
      pushExit(nodeKey, null);
    });

    return lines.join("\n").trim();
  }, [routeSnapshotsByNode, autoChecklistItems, linkLookup]);

  const updateEntries = useCallback((nextEntries) => {
    const normalized = normalizeEntries(nextEntries);
    const xml = entriesToMasterRoutesXml(normalized);
    setEditableEntries(normalized);
    setMasterRoutesMsg("");
    emitMasterRoutesChange(xml);
  }, [emitMasterRoutesChange]);

  const persistMasterRoutes = useCallback(
    async (
      xml,
      { successMsg = "Saved route-links master ✅", allowEmpty = false, extra = {} } = {}
    ) => {
      const pid = String(podcastId || "").trim();
      const nid = String(nodeId || "").trim();

      if (!pid) {
        setMasterRoutesMsg("Missing podcastId.");
        return false;
      }
      if (!nid) {
        setMasterRoutesMsg("Pick a location first so we know the nearest node.");
        return false;
      }

      const payload = String(xml || "").trim();
      if (!payload && !allowEmpty) {
        setMasterRoutesMsg("Route-links master is empty.");
        return false;
      }

      setIsSavingMasterRoutes(true);
      try {
        const db = getFirestore();
        const masterRef = getMasterRoutesDocRef(db, pid, nid);
        await setDoc(
          masterRef,
          {
            xml: payload,
            nodeId: nid,
            updatedAt: serverTimestamp(),
            ...extra,
          },
          { merge: true }
        );
        setMasterRoutesMsg(successMsg);
        setNeedsMasterRoutesBootstrap(false);
        return true;
      } catch (err) {
        console.error("Failed to save route-links master:", err);
        setMasterRoutesMsg("Failed to save route-links master.");
        return false;
      } finally {
        setIsSavingMasterRoutes(false);
      }
    },
    [podcastId, nodeId]
  );

  useEffect(() => {
    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();

    if (!pid || !nid) {
      setNeedsMasterRoutesBootstrap(false);
      emitMasterRoutesChange("");
      return;
    }

    let cancelled = false;
    setIsLoadingMasterRoutes(true);
    setMasterRoutesMsg("");

    const db = getFirestore();
    const masterRef = getMasterRoutesDocRef(db, pid, nid);

    (async () => {
      try {
        const snap = await getDoc(masterRef);
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() || {};
          const xmlVal = String(data.xml || "");
          const parsed = parseMasterRoutesXml(xmlVal);
          setEditableEntries(normalizeEntries(parsed.entries));
          emitMasterRoutesChange(xmlVal);
          setNeedsMasterRoutesBootstrap(false);
        } else {
          setEditableEntries([]);
          emitMasterRoutesChange("");
          setNeedsMasterRoutesBootstrap(true);
        }
      } catch (err) {
        console.error("Failed loading master routes doc:", err);
        if (!cancelled) setMasterRoutesMsg("Failed to load route-links master.");
      } finally {
        if (!cancelled) setIsLoadingMasterRoutes(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [podcastId, nodeId, emitMasterRoutesChange]);

  useEffect(() => {
    if (editableEntries.length === 0) {
      setExpandedExitIdx(null);
      setEditingExitIdx(null);
      setSelectedExitKeys(new Set());
      return;
    }
    if (expandedExitIdx != null && expandedExitIdx >= editableEntries.length) {
      setExpandedExitIdx(null);
    }
    if (editingExitIdx != null && editingExitIdx >= editableEntries.length) {
      setEditingExitIdx(null);
    }
    setSelectedExitKeys((current) => {
      const valid = new Set(editableEntries.map((entry, idx) => getEntrySelectionKey(entry, idx)));
      const next = new Set([...current].filter((key) => valid.has(key)));
      return next.size === current.size ? current : next;
    });
  }, [editableEntries, expandedExitIdx, editingExitIdx, getEntrySelectionKey]);

  useEffect(() => {
    if (typeof onActiveDestinationChange !== "function") return;
    if (expandedExitIdx == null) {
      onActiveDestinationChange(null);
      return;
    }
    const entry = editableEntries[expandedExitIdx];
    const nodeId = String(entry?.id || entry?.key || "").trim();
    onActiveDestinationChange(nodeId || null);
  }, [expandedExitIdx, editableEntries, onActiveDestinationChange]);

  useEffect(() => {
    if (!needsMasterRoutesBootstrap) return;
    if (!canRegenerateMasterRoutes) return;
    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();
    if (!pid || !nid) return;

    const xml = buildMasterRoutesXml();
    if (!xml) return;

    const parsed = parseMasterRoutesXml(xml);
    setEditableEntries(normalizeEntries(parsed.entries));
    emitMasterRoutesChange(xml);
    setMasterRoutesMsg("Auto-created from current routes.");
    (async () => {
      const ok = await persistMasterRoutes(xml, {
        successMsg: "Created route-links master from current routes ✅",
        extra: { autoGeneratedAt: serverTimestamp() },
      });
      if (ok) emitMasterRoutesSaved("bootstrap-autocreate");
    })();
  }, [
    needsMasterRoutesBootstrap,
    canRegenerateMasterRoutes,
    podcastId,
    nodeId,
    buildMasterRoutesXml,
    persistMasterRoutes,
    emitMasterRoutesChange,
    emitMasterRoutesSaved,
  ]);

  const persistEntriesNow = useCallback(
    async ({
      entries = editableEntries,
      successMsg = "Saved route-links master ✅",
      allowEmpty = false,
      reason = "master-routes-save",
    } = {}) => {
      const payload = entriesToMasterRoutesXml(entries);
      emitMasterRoutesChange(payload);
      const ok = await persistMasterRoutes(payload, { successMsg, allowEmpty });
      if (ok) emitMasterRoutesSaved(reason);
      return ok;
    },
    [editableEntries, emitMasterRoutesChange, persistMasterRoutes, emitMasterRoutesSaved]
  );

  const handleRegenerateMasterRoutes = () => {
    const xml = buildMasterRoutesXml();
    if (!xml) {
      setMasterRoutesMsg("Generate the routes first to regenerate route-links master.");
      return;
    }
    const parsed = parseMasterRoutesXml(xml);
    setEditableEntries(normalizeEntries(parsed.entries));
    emitMasterRoutesChange(xml);
    setMasterRoutesMsg("Regenerated from current routes. Save to persist.");
  };

  const addExit = () => {
    const nextEntries = [
      ...editableEntries,
      { id: "", title: "", key: "", order: String(editableEntries.length + 1), links: [{ id: "", name: "" }] },
    ];
    updateEntries(nextEntries);
    const newIndex = nextEntries.length - 1;
    setExpandedExitIdx(newIndex);
    setEditingExitIdx(newIndex);
  };

  const removeExit = async (exitIdx) => {
    const nextEntries = reindexEntries(editableEntries.filter((_, idx) => idx !== exitIdx));
    updateEntries(nextEntries);
    setSelectedExitKeys((current) => {
      const removedKey = getEntrySelectionKey(editableEntries[exitIdx], exitIdx);
      const next = new Set(current);
      next.delete(removedKey);
      return next;
    });
    setExpandedExitIdx((current) => {
      if (current == null) return null;
      if (current === exitIdx) return null;
      if (current > exitIdx) return current - 1;
      return current;
    });
    setEditingExitIdx((current) => {
      if (current == null) return null;
      if (current === exitIdx) return null;
      if (current > exitIdx) return current - 1;
      return current;
    });
    await persistEntriesNow({
      entries: nextEntries,
      allowEmpty: true,
      successMsg: "Removed exit and saved ✅",
      reason: "destination-remove-save",
    });
  };

  const toggleExitSelected = (entry, idx) => {
    const selectionKey = getEntrySelectionKey(entry, idx);
    setSelectedExitKeys((current) => {
      const next = new Set(current);
      if (next.has(selectionKey)) {
        next.delete(selectionKey);
      } else {
        next.add(selectionKey);
      }
      return next;
    });
  };

  const clearSelectedExits = () => {
    setSelectedExitKeys(new Set());
  };

  const deleteSelectedExits = async () => {
    if (!selectedExitKeys.size || isSavingMasterRoutes || isAnyRouteEditActive) return;
    const nextEntries = reindexEntries(
      editableEntries.filter((entry, idx) => !selectedExitKeys.has(getEntrySelectionKey(entry, idx)))
    );
    updateEntries(nextEntries);
    setExpandedExitIdx(null);
    setEditingExitIdx(null);
    setSelectedExitKeys(new Set());
    await persistEntriesNow({
      entries: nextEntries,
      allowEmpty: true,
      successMsg: "Deleted selected exits ✅",
      reason: "destination-bulk-remove-save",
    });
  };

  const moveExit = async (exitIdx, direction) => {
    const targetIdx = exitIdx + direction;
    if (targetIdx < 0 || targetIdx >= editableEntries.length || isSavingMasterRoutes) return;

    const nextEntries = [...editableEntries];
    [nextEntries[exitIdx], nextEntries[targetIdx]] = [nextEntries[targetIdx], nextEntries[exitIdx]];
    const reindexed = reindexEntries(nextEntries);
    updateEntries(reindexed);
    setExpandedExitIdx((current) => {
      if (current === exitIdx) return targetIdx;
      if (current === targetIdx) return exitIdx;
      return current;
    });
    setEditingExitIdx((current) => {
      if (current === exitIdx) return targetIdx;
      if (current === targetIdx) return exitIdx;
      return current;
    });
    await persistEntriesNow({
      entries: reindexed,
      successMsg: "Updated exit order ✅",
      reason: "destination-order-save",
    });
  };

  const updateExitField = (exitIdx, field, value) => {
    updateEntries(
      editableEntries.map((entry, idx) =>
        idx === exitIdx ? { ...entry, [field]: value } : entry
      )
    );
  };

  const addLink = (exitIdx) => {
    updateEntries(
      editableEntries.map((entry, idx) =>
        idx === exitIdx
          ? { ...entry, links: [...(entry.links || []), { id: "", name: "" }] }
          : entry
      )
    );
  };

  const removeLink = (exitIdx, linkIdx) => {
    updateEntries(
      editableEntries.map((entry, idx) =>
        idx === exitIdx
          ? { ...entry, links: (entry.links || []).filter((_, i) => i !== linkIdx) }
          : entry
      )
    );
  };

  const updateLinkField = (exitIdx, linkIdx, field, value) => {
    updateEntries(
      editableEntries.map((entry, idx) => {
        if (idx !== exitIdx) return entry;
        const links = (entry.links || []).map((link, i) =>
          i === linkIdx ? { ...link, [field]: value } : link
        );
        return { ...entry, links };
      })
    );
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid #d1d5db",
    background: "#f3f4f6",
    fontSize: 14,
    lineHeight: 1.2,
    color: "#111827",
    boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block",
    fontSize: 14,
    color: "#6b778c",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };
  const fieldLabelStyle = { display: "block", fontSize: 13, color: "#6b778c", marginBottom: 6, fontWeight: 600 };
  const iconBtnStyle = {
    border: "none",
    background: "transparent",
    color: "#111827",
    fontSize: 15,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    padding: "6px 10px",
  };
  const handleToggleExpand = (idx) => {
    setExpandedExitIdx((current) => (current === idx ? null : idx));
  };

  const isRouteEditActiveForEntry = (entry) =>
    !!routeEditState?.isActive &&
    String(routeEditState?.nodeId || "") === String(entry?.id || entry?.key || "");
  const isAnyRouteEditActive = !!routeEditState?.isActive;
  const selectedExitCount = selectedExitKeys.size;

  return (
    <section
      aria-label="Route links master"
      style={{
        marginBottom: 28,
        padding: 14,
        background: "#e5e7eb",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <h3 style={{ margin: 0, fontSize: 20, color: "#111827" }}>Exit routes</h3>
      {isLoadingMasterRoutes && (
        <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>Loading exit routes…</div>
      )}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div style={{ color: "#4b5563", fontSize: 14 }}>Structured editor view. XML is generated automatically.</div>
        <button
          type="button"
          onClick={addExit}
          style={{
            border: "1px solid #cbd5e1",
            background: "#fff",
            borderRadius: 999,
            padding: "10px 16px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          + Add exit
        </button>
      </div>
      {selectedExitCount > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            border: "1px solid #cbd5e1",
            borderRadius: 12,
            background: "#fff",
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: "#334155" }}>
            {selectedExitCount} selected
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={deleteSelectedExits}
              disabled={isSavingMasterRoutes || isAnyRouteEditActive}
              style={{
                border: "1px solid #fecaca",
                background: "#fee2e2",
                color: "#991b1b",
                borderRadius: 999,
                padding: "7px 12px",
                fontWeight: 700,
                cursor: isSavingMasterRoutes || isAnyRouteEditActive ? "not-allowed" : "pointer",
                opacity: isSavingMasterRoutes || isAnyRouteEditActive ? 0.55 : 1
              }}
            >
              Delete selected
            </button>
            <button
              type="button"
              onClick={clearSelectedExits}
              style={iconBtnStyle}
            >
              Clear
            </button>
          </div>
        </div>
      )}
      <div style={{ marginTop: 14, display: "grid", gap: 14 }}>
        {editableEntries.map((entry, exitIdx) => {
          const selectionKey = getEntrySelectionKey(entry, exitIdx);
          const isSelected = selectedExitKeys.has(selectionKey);
          return (
          <div
            key={`exit-${exitIdx}`}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 16,
              background: "#f3f4f6",
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              onClick={() => handleToggleExpand(exitIdx)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "14px 16px",
                border: "none",
                background: "transparent",
                display: "grid",
                gridTemplateColumns: "28px 52px minmax(0, 1fr) auto auto",
                alignItems: "center",
                columnGap: 12,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={isAnyRouteEditActive}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleExitSelected(entry, exitIdx)}
                aria-label={`Select ${entry.title || entry.id || `exit ${exitIdx + 1}`}`}
                style={{ width: 18, height: 18, cursor: isAnyRouteEditActive ? "not-allowed" : "pointer" }}
              />
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: "#dbe1ea",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2e63c7",
                  fontSize: 18,
                }}
              >
                ⌖
              </div>
              <div>
                <div style={{ fontSize: 18, color: "#111827", fontWeight: 700, lineHeight: 1.2 }}>
                  {entry.title || `Exit ${entry.id || exitIdx + 1}`}
                </div>
                <div style={{ marginTop: 2, fontSize: 13, color: "#6b778c" }}>
                  Order {entry.order || exitIdx + 1} · Node {entry.id || "—"} · Key {entry.key || "—"}
                </div>
              </div>
              <div
                style={{
                  background: "#dde4ef",
                  color: "#1f4fa6",
                  borderRadius: 999,
                  padding: "6px 12px",
                  fontSize: 14,
                  fontWeight: 700,
                  minWidth: 58,
                  textAlign: "center",
                }}
              >
                ↪ {(entry.links || []).filter((link) => String(link?.id || link?.name || "").trim()).length}
              </div>
              <span style={{ color: "#6b7280", fontSize: 20, paddingLeft: 6 }}>
                {expandedExitIdx === exitIdx ? "⌄" : "›"}
              </span>
            </button>
            {expandedExitIdx === exitIdx && (
              <div style={{ borderTop: "1px solid #d1d5db", padding: "14px 16px 16px" }}>
                {isRouteEditActiveForEntry(entry) && (
                  <div style={{ marginBottom: 12, fontSize: 13, color: "#334155" }}>
                    Route edit mode: click links on the map to add/remove. Use flip for opposite direction.
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
                  <p style={labelStyle}>Exit details</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {!isAnyRouteEditActive && (
                      <>
                        <button
                          type="button"
                          onClick={() => moveExit(exitIdx, -1)}
                          style={{ ...iconBtnStyle, opacity: exitIdx === 0 ? 0.45 : 1 }}
                          disabled={exitIdx === 0 || isSavingMasterRoutes}
                        >
                          ↑ Up
                        </button>
                        <button
                          type="button"
                          onClick={() => moveExit(exitIdx, 1)}
                          style={{ ...iconBtnStyle, opacity: exitIdx === editableEntries.length - 1 ? 0.45 : 1 }}
                          disabled={exitIdx === editableEntries.length - 1 || isSavingMasterRoutes}
                        >
                          ↓ Down
                        </button>
                      </>
                    )}
                    {!isAnyRouteEditActive && (editingExitIdx === exitIdx ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await persistEntriesNow({
                            entries: editableEntries,
                            successMsg: "Saved destination edits ✅",
                            reason: "destination-edit-save",
                          });
                          if (ok) setEditingExitIdx(null);
                        }}
                        style={{
                          border: "2px solid #2a63c6",
                          background: "#f8fafc",
                          color: "#111827",
                          borderRadius: 999,
                          padding: "7px 14px",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: isSavingMasterRoutes ? "not-allowed" : "pointer",
                        }}
                        disabled={isSavingMasterRoutes}
                      >
                        {isSavingMasterRoutes ? "Saving..." : "✓ Done"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingExitIdx(exitIdx)}
                        style={iconBtnStyle}
                      >
                        ✎ Edit
                      </button>
                    ))}
                    {!isAnyRouteEditActive && (
                    <button
                      type="button"
                      onClick={() => removeExit(exitIdx)}
                      style={{ ...iconBtnStyle, color: "#dc2626" }}
                      disabled={isSavingMasterRoutes}
                    >
                      🗑 Remove
                    </button>
                    )}
                    {!isRouteEditActiveForEntry(entry) ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (typeof onStartRouteEdit === "function") {
                            onStartRouteEdit({
                              nodeId: String(entry?.id || entry?.key || "").trim(),
                            });
                          }
                        }}
                        style={iconBtnStyle}
                      >
                        ⤴ Edit route
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          if (typeof onSaveRouteEdit !== "function") return;
                          const nodeId = String(entry?.id || entry?.key || "").trim();
                          setIsSavingRouteEdit(true);
                          try {
                            const result = await onSaveRouteEdit({ nodeId });
                            if (!result?.ok) return;
                            const nextLinks = Array.isArray(result?.links) ? result.links : [];
                            const nextEntries = editableEntries.map((row) => {
                              const rowNodeId = String(row?.id || row?.key || "").trim();
                              return rowNodeId === nodeId ? { ...row, links: nextLinks } : row;
                            });
                            const changed = nextEntries.some((row, idx) =>
                              JSON.stringify(row?.links || []) !== JSON.stringify(editableEntries[idx]?.links || [])
                            );
                            if (!changed) return;
                            updateEntries(nextEntries);
                            await persistEntriesNow({
                              entries: nextEntries,
                              successMsg: "Saved route edit ✅",
                              reason: "route-edit-save",
                            });
                          } finally {
                            setIsSavingRouteEdit(false);
                          }
                        }}
                        style={{
                          border: "2px solid #2a63c6",
                          background: "#f8fafc",
                          color: "#111827",
                          borderRadius: 999,
                          padding: "7px 14px",
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: isSavingRouteEdit ? "not-allowed" : "pointer",
                        }}
                        disabled={isSavingRouteEdit}
                      >
                        {isSavingRouteEdit ? "Saving route..." : "✓ Save route"}
                      </button>
                    )}
                    {isRouteEditActiveForEntry(entry) && (
                      <button
                        type="button"
                        onClick={() => typeof onCancelRouteEdit === "function" && onCancelRouteEdit()}
                        style={iconBtnStyle}
                        disabled={isSavingRouteEdit}
                      >
                        Cancel
                      </button>
                    )}
                    {isRouteEditActiveForEntry(entry) && isSavingRouteEdit && (
                      <span style={{ fontSize: 12, color: "#475569" }}>Saving route...</span>
                    )}
                  </div>
                </div>

                {editingExitIdx === exitIdx ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
                    <label>
                      <span style={fieldLabelStyle}>Destination node ID</span>
                      <input
                        type="text"
                        value={entry.id}
                        onChange={(e) => updateExitField(exitIdx, "id", e.target.value)}
                        style={inputStyle}
                        placeholder="Destination node ID"
                      />
                    </label>
                    <label>
                      <span style={fieldLabelStyle}>Exit title</span>
                      <input
                        type="text"
                        value={entry.title}
                        onChange={(e) => updateExitField(exitIdx, "title", e.target.value)}
                        style={inputStyle}
                        placeholder="Exit title"
                      />
                    </label>
                    <label>
                      <span style={fieldLabelStyle}>Key</span>
                      <input
                        type="text"
                        value={entry.key}
                        onChange={(e) => updateExitField(exitIdx, "key", e.target.value)}
                        style={inputStyle}
                        placeholder="Key"
                      />
                    </label>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
                    <div>
                      <div style={fieldLabelStyle}>Destination node ID</div>
                      <div style={{ fontSize: 14, color: "#111827" }}>{entry.id || "—"}</div>
                    </div>
                    <div>
                      <div style={fieldLabelStyle}>Exit title</div>
                      <div style={{ fontSize: 14, color: "#111827" }}>{entry.title || "—"}</div>
                    </div>
                    <div>
                      <div style={fieldLabelStyle}>Key</div>
                      <div style={{ fontSize: 14, color: "#111827" }}>{entry.key || "—"}</div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <p style={labelStyle}>Route links</p>
                    {editingExitIdx === exitIdx && !isRouteEditActiveForEntry(entry) && (
                      <button
                        type="button"
                        onClick={() => addLink(exitIdx)}
                        style={iconBtnStyle}
                      >
                        + Add link
                      </button>
                    )}
                  </div>

                  {isRouteEditActiveForEntry(entry) ? (
                    <div style={{ display: "grid", gap: 6 }}>
                      {(routeEditState?.draftLinks || []).map((link, idx) => (
                        <div
                          key={`draft-${link.id}-${idx}`}
                          style={{
                            border: "1px solid #cbd5e1",
                            borderRadius: 10,
                            background: "#fff",
                            padding: "8px 10px",
                            display: "grid",
                            gridTemplateColumns: "minmax(80px, 120px) minmax(120px, 1fr) auto auto",
                            gap: 8,
                            alignItems: "center",
                          }}
                        >
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#1f2937" }}>{link.id}</span>
                          <span style={{ fontSize: 12, color: "#334155" }}>
                            {link.name || "Unnamed"} ({link.startNodeId}→{link.endNodeId})
                          </span>
                          <button
                            type="button"
                            onClick={() => typeof onFlipRouteEditLink === "function" && onFlipRouteEditLink({ index: idx })}
                            style={{ ...iconBtnStyle, padding: "4px 8px", fontSize: 12 }}
                            disabled={!link.canFlip || isSavingRouteEdit}
                            title={link.canFlip ? "Flip to reverse link" : "No reverse link found"}
                          >
                            ⇄ Flip
                          </button>
                          <button
                            type="button"
                            onClick={() => typeof onRemoveRouteEditLink === "function" && onRemoveRouteEditLink({ index: idx })}
                            style={{ ...iconBtnStyle, padding: "4px 8px", fontSize: 12, color: "#b91c1c" }}
                            disabled={isSavingRouteEdit}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      {(routeEditState?.draftLinks || []).length === 0 && (
                        <div style={{ fontSize: 12, color: "#6b7280" }}>No selected links yet.</div>
                      )}
                    </div>
                  ) : editingExitIdx === exitIdx ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      {(entry.links || []).map((link, linkIdx) => (
                        <div
                          key={`exit-${exitIdx}-link-${linkIdx}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(120px, 180px) minmax(200px, 1fr) auto",
                            gap: 10,
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="text"
                            value={link.id}
                            onChange={(e) => updateLinkField(exitIdx, linkIdx, "id", e.target.value)}
                            style={inputStyle}
                            placeholder="Link ID"
                          />
                          <input
                            type="text"
                            value={link.name}
                            onChange={(e) => updateLinkField(exitIdx, linkIdx, "name", e.target.value)}
                            style={inputStyle}
                            placeholder="Street / Link name"
                          />
                          <button
                            type="button"
                            onClick={() => removeLink(exitIdx, linkIdx)}
                            style={{ ...iconBtnStyle, color: "#6b778c" }}
                            aria-label={`Remove link ${linkIdx + 1}`}
                          >
                            🗑
                          </button>
                        </div>
                      ))}
                      {(entry.links || []).length === 0 && (
                        <div style={{ color: "#6b7280", fontSize: 14 }}>No links yet.</div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        border: "1px solid #cfd4dd",
                        borderRadius: 14,
                        overflow: "hidden",
                        background: "#f3f4f6",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "180px minmax(180px, 1fr)",
                          gap: 0,
                          borderBottom: "1px solid #cfd4dd",
                          fontSize: 13,
                          color: "#6b778c",
                          fontWeight: 600,
                          padding: "10px 14px",
                        }}
                      >
                        <span>Link ID</span>
                        <span>Street / Link name</span>
                      </div>
                      {(entry.links || []).filter((link) => String(link?.id || link?.name || "").trim()).map((link, linkIdx, arr) => (
                        <div
                          key={`exit-${exitIdx}-view-link-${linkIdx}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "180px minmax(180px, 1fr)",
                            padding: "10px 14px",
                            borderBottom: linkIdx < arr.length - 1 ? "1px solid #cfd4dd" : "none",
                            fontSize: 14,
                            color: "#111827",
                          }}
                        >
                          <span>{link.id || "—"}</span>
                          <span>{link.name || "—"}</span>
                        </div>
                      ))}
                      {(entry.links || []).filter((link) => String(link?.id || link?.name || "").trim()).length === 0 && (
                        <div style={{ padding: "14px 24px", color: "#6b7280", fontSize: 14 }}>No route links.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          );
        })}
        {editableEntries.length === 0 && (
          <div
            style={{
              border: "1px dashed #cbd5e1",
              borderRadius: 12,
              padding: 16,
              color: "#6b7280",
              background: "#fff",
              fontSize: 14,
            }}
          >
            No exits yet. Use <strong>Add exit</strong> or click <strong>Regenerate</strong> to auto-fill from map routes.
          </div>
        )}
      </div>
      <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <button
          onClick={handleRegenerateMasterRoutes}
          disabled={!canRegenerateMasterRoutes || isSavingMasterRoutes}
          style={{
            padding: "9px 16px",
            borderRadius: 8,
            border: "1px solid #94a3b8",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 700,
            cursor: !canRegenerateMasterRoutes || isSavingMasterRoutes ? "not-allowed" : "pointer",
          }}
        >
          Regenerate
        </button>
        {!!masterRoutesMsg && <span style={{ color: "#111827", fontSize: 14 }}>{masterRoutesMsg}</span>}
      </div>
      {!canRegenerateMasterRoutes && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#555" }}>
          Generate routes (via the map) to enable regeneration.
        </div>
      )}
    </section>
  );
};

export default MasterRoutesPanel;
